import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Smartphone, Tablet, Monitor, HardDrive, Shield, ChevronRight,
  MapPin, Clock, Star, CheckCircle, ArrowRight, Zap, Award, Phone,
} from '../../lib/icons';
import SEOHead from '../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'GameFix Solutions — Phone & Apple Repair',
  description: 'Premium iPhone, iPad, Samsung, and MacBook repair in Temecula Valley and North County San Diego.',
  telephone: '+19512168534',
  email: 'info@gamefixsolutions.com',
  url: 'https://gamefixsolutions.com/phone-repair',
  areaServed: [
    'Temecula, CA', 'Murrieta, CA', 'Menifee, CA', 'Winchester, CA',
    'Oceanside, CA', 'Carlsbad, CA', 'Vista, CA', 'San Marcos, CA', 'Escondido, CA', 'Encinitas, CA',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Phone & Apple Repair Services',
    itemListElement: [
      { '@type': 'Offer', name: 'iPhone Screen Replacement', priceCurrency: 'USD', price: '89' },
      { '@type': 'Offer', name: 'iPhone Battery Replacement', priceCurrency: 'USD', price: '99' },
      { '@type': 'Offer', name: 'Samsung Screen Replacement', priceCurrency: 'USD', price: '149' },
      { '@type': 'Offer', name: 'iPad Screen Replacement', priceCurrency: 'USD', price: '179' },
    ],
  },
};

const SERVICES = [
  {
    icon: Smartphone,
    title: 'iPhone Repair',
    description: 'Screen, battery, charging port, back glass, camera, and more. Most repairs completed same day in Temecula.',
    items: ['Screen Replacement', 'Battery Service', 'Charging Port', 'Back Glass', 'Camera Repair', 'Water Damage'],
    href: '/phone-repair/temecula',
    color: 'from-[#0EA5E9] to-[#2563EB]',
    accent: '#0EA5E9',
  },
  {
    icon: Tablet,
    title: 'iPad Repair',
    description: 'All iPad models — screen replacement, battery service, charging ports, and full diagnostics.',
    items: ['Screen Replacement', 'Battery Service', 'Charging Port', 'Camera Repair', 'Diagnostic'],
    href: '/phone-repair/temecula',
    color: 'from-[#8B5CF6] to-[#6D28D9]',
    accent: '#8B5CF6',
  },
  {
    icon: Smartphone,
    title: 'Samsung Repair',
    description: 'Galaxy S and Z series repair including AMOLED screens, batteries, charging ports, and back glass.',
    items: ['Screen Replacement', 'Battery Service', 'Charging Port', 'Back Glass', 'Diagnostic'],
    href: '/phone-repair/temecula',
    color: 'from-[#10B981] to-[#059669]',
    accent: '#10B981',
  },
  {
    icon: Monitor,
    title: 'MacBook Repair',
    description: 'MacBook Air and Pro repair — screen, battery, SSD upgrades, charging issues, and logic board diagnostics.',
    items: ['Screen Replacement', 'Battery Service', 'SSD Upgrades', 'Charging Issues', 'No Power Diagnostics'],
    href: '/phone-repair/temecula',
    color: 'from-[#F59E0B] to-[#D97706]',
    accent: '#F59E0B',
  },
  {
    icon: HardDrive,
    title: 'Data Recovery',
    description: 'Recovering photos, files, and documents from iPhones, iPads, MacBooks, SSDs, and external drives.',
    items: ['iPhone & iPad', 'MacBook & SSD', 'External Drives', 'Flash Drives', 'Liquid Damage Devices'],
    href: '/phone-repair/data-recovery',
    color: 'from-[#EF4444] to-[#DC2626]',
    accent: '#EF4444',
  },
  {
    icon: Shield,
    title: 'Screen Protection',
    description: 'Tempered glass and ProtectionPro films by Madico. Applied professionally while your device is here.',
    items: ['Tempered Glass', 'Privacy Glass', 'ProtectionPro Ultra 2', 'ProtectionPro Matte', 'All Major Devices'],
    href: '/phone-repair/screen-protection',
    color: 'from-[#0EA5E9] to-[#10B981]',
    accent: '#0EA5E9',
  },
];

const QUALITY_POINTS = [
  {
    icon: Award,
    title: 'Quality Parts Only',
    body: 'We prioritize reliable, high-quality repair parts designed for long-term performance. We avoid ultra-budget components commonly associated with dim displays, touch issues, and reduced durability.',
  },
  {
    icon: Shield,
    title: 'Warranty-Backed Repairs',
    body: 'Every repair comes with a 90-day warranty minimum. Battery replacements include 12 months. We stand behind our work — no fine print.',
  },
  {
    icon: Clock,
    title: 'Fast Temecula Turnaround',
    body: 'Most iPhone and Samsung repairs are completed same day by appointment. We don\'t make you wait a week for a screen replacement.',
  },
  {
    icon: Star,
    title: 'Transparent Pricing',
    body: 'You\'ll know the price before we start. No surprise charges, no upselling you parts you don\'t need. If the repair isn\'t worth it, we\'ll tell you.',
  },
];

const FAQS = [
  {
    q: 'How long does an iPhone screen replacement take?',
    a: 'Most iPhone screen replacements are completed within 1–2 hours by appointment at our Temecula location. Same-day service is available for most models when parts are in stock.',
  },
  {
    q: 'Do you use original Apple parts?',
    a: 'We use high-quality aftermarket display panels that meet or exceed OEM specifications. We do not use bargain-bin screens — we prioritize parts that maintain proper brightness, color accuracy, and touch sensitivity.',
  },
  {
    q: 'Do you repair Samsung phones?',
    a: 'Yes. We repair Samsung Galaxy S and Z series devices including screen replacements, battery service, charging ports, and back glass. Bring it in or contact us to confirm your model.',
  },
  {
    q: 'What about North County San Diego service?',
    a: 'We provide appointment-based service throughout North County San Diego including Oceanside, Carlsbad, Vista, San Marcos, Escondido, and Encinitas. Turnaround time depends on scheduling and parts availability — contact us before booking.',
  },
  {
    q: 'Can you recover data from a water-damaged phone?',
    a: 'Sometimes. Data recovery from liquid-damaged devices depends on the severity of damage. We perform an honest diagnostic first — if recovery isn\'t viable, we\'ll tell you upfront. We never guarantee data recovery.',
  },
  {
    q: 'Is there a diagnostic fee?',
    a: 'Yes — $29 for phones, $39 for iPads and Samsung. The diagnostic fee is credited toward your repair cost if you choose to proceed with the work.',
  },
];

const TEMECULA_CITIES = ['Temecula', 'Murrieta', 'Menifee', 'Winchester'];
const NCSD_CITIES = ['Oceanside', 'Carlsbad', 'Vista', 'San Marcos', 'Escondido', 'Encinitas'];

const PhoneRepairHomePage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="bg-[#050B14] min-h-screen font-sans">
      <SEOHead
        title="iPhone, Samsung & Apple Repair | Temecula & North County SD | GameFix Solutions"
        description="Premium iPhone, iPad, Samsung, and MacBook repair in Temecula, CA and North County San Diego. Same-day repairs by appointment. High-quality parts, 90-day warranty."
        keywords="iphone repair temecula, samsung repair temecula, ipad repair, macbook repair, phone repair north county san diego, screen replacement temecula"
        canonical="https://gamefixsolutions.com/phone-repair"
        jsonLD={schema}
      />

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] to-[#050B14]" />
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(14,165,233,0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(16,185,129,0.10) 0%, transparent 60%)' }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-[#0EA5E9] text-xs font-semibold tracking-wide uppercase">Temecula · North County San Diego</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              Premium Phone &<br />
              <span className="bg-gradient-to-r from-[#0EA5E9] to-[#10B981] bg-clip-text text-transparent">
                Apple Repair
              </span>
            </h1>

            <p className="text-slate-300 text-lg sm:text-xl leading-relaxed mb-8 max-w-2xl">
              Quality-first iPhone, iPad, Samsung, and MacBook repair. Most Temecula repairs completed
              same day by appointment — no bargain components, no shortcuts.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/book-repair"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold transition-colors">
                Book a Repair <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="tel:+19512168534"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-[#0EA5E9]/60 text-slate-300 hover:text-white font-semibold transition-colors">
                <Phone className="h-4 w-4" /> (951) 216-8534
              </a>
            </div>

            <div className="flex flex-wrap gap-4">
              {['Same-day Temecula repairs', '90-day warranty', 'Quality parts only', 'No bargain shortcuts'].map(p => (
                <div key={p} className="flex items-center gap-1.5 text-sm text-slate-400">
                  <CheckCircle className="h-3.5 w-3.5 text-[#10B981] shrink-0" />
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Grid ─────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Services We Offer</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Everything from cracked screens to data recovery — done right.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map(svc => (
              <Link
                key={svc.title}
                to={svc.href}
                className="group bg-[#0A1628] border border-slate-700/50 hover:border-[#0EA5E9]/40 rounded-2xl p-6 transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${svc.color} flex items-center justify-center mb-4`}>
                  <svc.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2 group-hover:text-[#0EA5E9] transition-colors">{svc.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{svc.description}</p>
                <ul className="space-y-1">
                  {svc.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="w-1 h-1 rounded-full bg-slate-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-center gap-1 text-[#0EA5E9] text-sm font-semibold">
                  Learn more <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ─────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#0A1628]/60 border-y border-slate-800/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Why Customers Choose Us</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              We're not competing on price. We're competing on quality.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {QUALITY_POINTS.map(pt => (
              <div key={pt.title} className="flex gap-4 p-6 bg-[#050B14] rounded-2xl border border-slate-800/60">
                <div className="w-10 h-10 rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex items-center justify-center shrink-0">
                  <pt.icon className="h-5 w-5 text-[#0EA5E9]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1.5">{pt.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{pt.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Locations ─────────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Service Areas</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Based in Temecula. Appointment service throughout North County San Diego.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Temecula Region */}
            <div className="bg-gradient-to-br from-[#0A1F3A] to-[#0A1628] border border-[#0EA5E9]/25 rounded-2xl p-7">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-[#10B981] text-xs font-bold uppercase tracking-widest">Primary Location</span>
              </div>
              <h3 className="text-white text-xl font-bold mb-1">Temecula Valley</h3>
              <p className="text-[#0EA5E9] text-sm font-semibold mb-3">Same-day repairs available by appointment</p>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                Our home base. Fast turnaround, most parts in stock. iPhone and Samsung
                screen replacements are typically completed the same day. Book an appointment
                and drop off your device.
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {TEMECULA_CITIES.map(city => (
                  <Link
                    key={city}
                    to={`/phone-repair/${city.toLowerCase()}`}
                    className="px-3 py-1 rounded-lg bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 text-[#0EA5E9] text-xs font-medium hover:bg-[#0EA5E9]/20 transition-colors"
                  >
                    {city}
                  </Link>
                ))}
              </div>
              <Link to="/phone-repair/temecula" className="inline-flex items-center gap-1.5 text-[#0EA5E9] text-sm font-semibold hover:text-white transition-colors">
                <MapPin className="h-3.5 w-3.5" /> Temecula service details <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* North County SD */}
            <div className="bg-gradient-to-br from-[#1A1428] to-[#0A1628] border border-purple-500/20 rounded-2xl p-7">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-purple-400" />
                <span className="text-purple-400 text-xs font-bold uppercase tracking-widest">Secondary — Appointment-Based</span>
              </div>
              <h3 className="text-white text-xl font-bold mb-1">North County San Diego</h3>
              <p className="text-purple-400 text-sm font-semibold mb-3">Appointment-based — turnaround varies</p>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                We serve North County San Diego by appointment. We're not a storefront — scheduling
                and parts availability determine turnaround. Contact us before booking to set
                realistic expectations.
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {NCSD_CITIES.map(city => (
                  <Link
                    key={city}
                    to={`/phone-repair/${city.toLowerCase().replace(' ', '-')}`}
                    className="px-3 py-1 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-medium hover:bg-purple-500/20 transition-colors"
                  >
                    {city}
                  </Link>
                ))}
              </div>
              <Link to="/phone-repair/oceanside" className="inline-flex items-center gap-1.5 text-purple-400 text-sm font-semibold hover:text-white transition-colors">
                <MapPin className="h-3.5 w-3.5" /> North County service details <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Screen Protection Callout ─────────────────────────────────────────── */}
      <section className="py-12 bg-gradient-to-r from-[#0EA5E9]/10 to-[#10B981]/10 border-y border-[#0EA5E9]/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#10B981] flex items-center justify-center shrink-0">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-white text-xl font-bold mb-1">Protect Your New Screen</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              After a screen replacement, it's the perfect time to add protection. We offer tempered glass, privacy glass,
              and ProtectionPro Ultra 2 films by Madico — applied professionally while your device is here.
            </p>
          </div>
          <Link to="/phone-repair/screen-protection"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0EA5E9]/20 hover:bg-[#0EA5E9]/30 border border-[#0EA5E9]/40 text-[#0EA5E9] font-semibold text-sm transition-colors whitespace-nowrap">
            View Protection Options <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ── Buyback Callout ───────────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0A1628] border border-slate-700/60 rounded-2xl p-7 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div className="flex-1">
              <p className="text-[#10B981] text-xs font-bold uppercase tracking-widest mb-1">Device Buyback</p>
              <h3 className="text-white text-xl font-bold mb-2">Sell Your iPhone</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Upgrading your iPhone? Get an instant estimate on what we'll pay. Working or broken — we buy iPhones, iPads, and MacBooks.
              </p>
            </div>
            <Link to="/buyback"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/40 text-[#10B981] font-semibold text-sm transition-colors whitespace-nowrap">
              Get an Estimate <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#0A1628]/60 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Common Questions</h2>
            <p className="text-slate-400">Answers before you reach out.</p>
          </div>

          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-[#050B14] border border-slate-800/60 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-white font-semibold text-sm pr-4">{faq.q}</span>
                  <span className={`shrink-0 w-5 h-5 rounded-full border border-slate-600 flex items-center justify-center text-slate-400 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 mb-6">
            <Zap className="h-3.5 w-3.5 text-[#10B981]" />
            <span className="text-[#10B981] text-xs font-semibold tracking-wide uppercase">Ready when you are</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Book Your Repair Today
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
            Temecula Valley — same-day by appointment. North County San Diego — reach out to schedule.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book-repair"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold transition-colors text-lg">
              Book a Repair <ArrowRight className="h-5 w-5" />
            </Link>
            <a href="tel:+19512168534"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-slate-600 hover:border-[#0EA5E9]/60 text-slate-300 hover:text-white font-semibold transition-colors text-lg">
              <Phone className="h-5 w-5" /> Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhoneRepairHomePage;
